import { ILesson } from '@dtos/Course'
import { useGetNextAvailableLessonData } from '@services/api/graphql/nextAvailableLessonData'
import { useGetPreviousAvailableLessonData } from '@services/api/graphql/previousAvailableLessonData'
import { useToastController } from '@tamagui/toast'
import { router, useLocalSearchParams } from 'expo-router'
import { useCallback, useState } from 'react'

interface IUseLessonNavigationParams {
  currentLesson: ILesson | null
  enrollmentId?: number | null
  hasPreviousContent: boolean
  hasNextContent: boolean
  resetNextAndPreviousContent: () => void
  /** Atualiza a aula corrente na tela (estado local + ids ativos). */
  onLessonChange: (lesson: ILesson) => void
}

export function useLessonNavigation({
  currentLesson,
  enrollmentId,
  hasPreviousContent,
  hasNextContent,
  resetNextAndPreviousContent,
  onLessonChange,
}: IUseLessonNavigationParams) {
  const [isLoadingNextOrPreviousLesson, setIsLoadingNextOrPreviousLesson] =
    useState(false)
  const { courseId, enrollmentId: enrollmentIdParam } = useLocalSearchParams()
  const toast = useToastController()

  const { getNextAvailableLessonData } = useGetNextAvailableLessonData(
    currentLesson?.id ?? 0,
    enrollmentId,
  )
  const { getPreviousAvailableLessonData } = useGetPreviousAvailableLessonData(
    currentLesson?.id ?? 0,
    enrollmentId,
  )

  const handlePreviousLessonPress = useCallback(async () => {
    // enrollmentId ausente é o preview do produtor, não estado inválido.
    if (!hasPreviousContent || !currentLesson?.id) return
    try {
      setIsLoadingNextOrPreviousLesson(true)
      const { data } = await getPreviousAvailableLessonData(
        currentLesson?.id,
        enrollmentId,
      )
      if (data?.previousAvailableLesson?.id) {
        onLessonChange(data.previousAvailableLesson)
        resetNextAndPreviousContent()
        router.setParams({
          courseId: courseId?.toString(),
          enrollmentId: enrollmentIdParam?.toString(),
          courseContentsId: data.previousAvailableLesson.courseContentId,
        })
      }
    } catch (error) {
      toast.show('Erro ao buscar informações da aula anterior', {
        message: 'Tente novamente mais tarde',
        type: 'error',
      })
    } finally {
      setIsLoadingNextOrPreviousLesson(false)
    }
  }, [
    hasPreviousContent,
    currentLesson?.id,
    enrollmentId,
    getPreviousAvailableLessonData,
    onLessonChange,
    resetNextAndPreviousContent,
    courseId,
    enrollmentIdParam,
    toast,
  ])

  const handleNextLessonPress = useCallback(async () => {
    if (!hasNextContent || !currentLesson?.id) return
    try {
      setIsLoadingNextOrPreviousLesson(true)
      const { data } = await getNextAvailableLessonData(
        currentLesson?.id,
        enrollmentId,
      )
      if (data?.nextAvailableLesson?.id) {
        onLessonChange(data.nextAvailableLesson)
        resetNextAndPreviousContent()
        router.setParams({
          courseId: courseId?.toString(),
          enrollmentId: enrollmentIdParam?.toString(),
          courseContentsId: data.nextAvailableLesson.courseContentId,
        })
      }
    } catch (error) {
      toast.show('Erro ao buscar informações da próxima aula', {
        message: 'Tente novamente mais tarde',
        type: 'error',
      })
    } finally {
      setIsLoadingNextOrPreviousLesson(false)
    }
  }, [
    hasNextContent,
    currentLesson?.id,
    enrollmentId,
    getNextAvailableLessonData,
    onLessonChange,
    resetNextAndPreviousContent,
    courseId,
    enrollmentIdParam,
    toast,
  ])

  return {
    handlePreviousLessonPress,
    handleNextLessonPress,
    isLoadingNextOrPreviousLesson,
  }
}
